import { useState, useEffect } from 'react';
import { useStytch, useStytchUser, useStytchSession } from '@stytch/react';
import { eventBus } from '@/modules/core/events';
import { events } from '../events';
import * as Sentry from '@sentry/browser';
import { captureError, safeJsonParse } from '@/utils/errorHandler';
import { 
  trackSignIn, 
  trackSignOut, 
  trackUserIdentification, 
  trackUserReset,
  trackVercelSignIn,
  trackVercelSignOut,
  trackVercelUserIdentification
} from '@/services/analytics';

export function useAuth() {
  const stytch = useStytch();
  const { user: stytchUser } = useStytchUser();
  const { session: stytchSession } = useStytchSession();

  const [loading, setLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);

  // Convert Stytch user to your app's format (email-as-identifier)
  const createAppUser = (stytchUser) => {
    if (!stytchUser) return null;

    return {
      id: stytchUser.user_id,
      email: stytchUser.emails?.[0]?.email || null,
      created_at: stytchUser.created_at,
    };
  };

  // Detect authentication method from Stytch user
  const getAuthMethod = (stytchUser) => {
    if (!stytchUser) return 'email';
    
    // Check for OAuth providers
    if (stytchUser.oauth_registrations && stytchUser.oauth_registrations.length > 0) {
      const provider = stytchUser.oauth_registrations[0].provider_type;
      // Map Stytch provider types to our tracking format
      if (provider === 'google') return 'google';
      if (provider === 'apple') return 'apple';
      return provider || 'oauth';
    }
    
    // Default to email if no OAuth registration found
    return 'email';
  };

  // Handle auth state changes
  useEffect(() => {
    console.log('[useAuth] useEffect running - stytchSession changed:', {
      hasSession: !!stytchSession,
      hasUser: !!stytchUser,
      hasStytch: !!stytch,
      currentHasSession: hasSession
    });
    
    let token = null;
    if (stytchSession && stytch) {
      try {
        const tokens = stytch.session.getTokens();
        token = tokens?.session_token || null;
        console.log('[useAuth] Got token:', {
          hasToken: !!token,
          tokenLength: token?.length
        });
      } catch (error) {
        console.error('[useAuth] Error getting session tokens:', error);
        token = null;
      }
    }

    const newSessionExists = !!token;
    const userExists = !!stytchUser;
    
    // Handle sign in event - only fire when transitioning from no session to session
    if (newSessionExists && userExists && !hasSession) {
      console.log('[useAuth] Sign in event');
      setHasSession(true);
      const appUser = createAppUser(stytchUser);
      const authMethod = getAuthMethod(stytchUser);
      
      if (appUser?.email) {
        eventBus.publish(events.USER_SIGNED_IN, { user: appUser });
        
        // Track sign-in with both PostHog and Vercel Analytics
        trackSignIn(authMethod);
        trackVercelSignIn(authMethod);
        
        // Identify user with both analytics platforms
        trackUserIdentification(appUser.id, {
          email: appUser.email
        });
        trackVercelUserIdentification(appUser.id, {
          email: appUser.email
        });
      }
    }
    
    // Handle sign out event - only fire when transitioning from session to no session
    if (!newSessionExists && hasSession) {
      console.log('[useAuth] Sign out event');
      setHasSession(false);
      eventBus.publish(events.USER_SIGNED_OUT, {});
      
      // Track sign-out and reset user identification for both platforms
      trackSignOut();
      trackVercelSignOut();
      trackUserReset();
    }

    setLoading(false);
  }, [stytchSession, stytchUser, stytch]);

  const signOut = async () => {
    try {
      await stytch.session.revoke();
    } catch (error) {
      console.error('Error signing out:', error);
      Sentry.captureException(error);
      throw error;
    }
  };

  // Helper function for authenticated API calls with automatic error capture
  const authFetch = async (url, options = {}) => {
    const method = options.method || 'GET';
    
    // Get token from Stytch client - no retries, just get current state
    let token = null;
    if (stytch) {
      try {
        const tokens = stytch.session.getTokens();
        token = tokens?.session_token || null;
      } catch (error) {
        console.error('[useAuth] Error getting session tokens in authFetch:', error);
      }
    }
    
    // If no token, user is not logged in
    if (!token || token === 'null' || token === 'undefined') {
      console.error('[useAuth] authFetch: No valid session token', { url, hasStytchClient: !!stytch });
      const error = new Error('No valid session token available. Please ensure you are logged in.');
      error.isAuthError = true;
      throw error;
    }
    
    const context = {
      action: `authFetch:${method}`,
      component: 'useAuth',
      extra: {
        url,
        method,
        hasBody: !!options.body,
      },
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      // Handle 401 Unauthorized - session expired or invalid
      if (response.status === 401) {
        console.log('[useAuth] Received 401 - session expired, logging out');
        // Revoke the session to clean up local state
        try {
          await stytch.session.revoke();
        } catch (revokeError) {
          // Session may already be invalid, that's fine
          console.log('[useAuth] Session revoke after 401:', revokeError.message);
        }
        const authError = new Error('Session expired. Please log in again.');
        authError.isAuthError = true;
        authError.status = 401;
        throw authError;
      }

      // If response is not ok, capture error with response details
      if (!response.ok) {
        let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
        let errorData = null;

        // Clone response so we can read it without consuming the original
        const responseClone = response.clone();
        
        try {
          errorData = await safeJsonParse(responseClone, {
            ...context,
            extra: {
              ...context.extra,
              status: response.status,
              statusText: response.statusText,
            },
          });
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, we'll use the status text
          // The parse error is already captured by safeJsonParse
        }

        const apiError = new Error(errorMessage);
        
        // Don't send expected authorization errors to Sentry
        const isExpectedAuthError = response.status === 403 && 
          (errorMessage.includes('Admin access required') || errorMessage.includes('Access denied'));
        
        if (!isExpectedAuthError) {
          captureError(apiError, {
            ...context,
            extra: {
              ...context.extra,
              status: response.status,
              statusText: response.statusText,
              errorData,
            },
            tags: {
              apiError: true,
              statusCode: response.status,
            },
          });
        }

        // Attach response to error for callers to inspect
        apiError.response = response;
        apiError.status = response.status;
        apiError.data = errorData;
        throw apiError;
      }

      return response;
    } catch (error) {
      // If it's already our API/auth error, re-throw it
      if (error.response || error.isAuthError) {
        throw error;
      }

      // Network errors, CORS errors, etc.
      const networkError = error instanceof TypeError && error.message.includes('fetch')
        ? new Error(`Network error: ${error.message}`)
        : error;

      captureError(networkError, {
        ...context,
        extra: {
          ...context.extra,
          originalError: error.message,
          errorType: error.constructor.name,
        },
        tags: {
          networkError: true,
        },
      });

      throw networkError;
    }
  };

  return {
    session: stytchSession,
    user: createAppUser(stytchUser),
    loading,
    signOut,
    authFetch
  };
}